import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import { booksApi } from '@/lib/api';
import BookAutocomplete from '@/components/app/BookAutocomplete';
import NotificationsDropdown from '@/components/app/NotificationsDropdown';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Bell, Menu, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function AppNavbar() {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const handleBookSelect = async (book) => {
    try {
      const stored = await booksApi.getOrCreate(book);
      navigate(`/books/${stored.id}`);
    } catch {
      // Fallback to google ID route if store fails
      navigate(`/books/google/${book.google_books_id}`);
    }
  };

  const handleSearch = (q) => {
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-cream border-b border-cream-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? '/feed' : '/'} className="flex items-center shrink-0">
            <img src="/logo.png" alt="ReadTheRoom" className="h-12 w-12 rounded-lg object-cover" />
          </Link>

          {/* Center: Book autocomplete search (hidden on mobile) */}
          <BookAutocomplete
            onSelect={handleBookSelect}
            onSearch={handleSearch}
            placeholder="Search books..."
            className="hidden md:block flex-1 max-w-sm mx-8"
          />

          {/* Add Books */}
          {user && (
            <Button
              onClick={() => navigate('/search')}
              className="hidden md:flex items-center gap-1.5 bg-terra hover:bg-terra/90 text-cream text-sm font-semibold rounded-full px-4 h-8"
            >
              <Plus className="w-4 h-4" />
              Add Books
            </Button>
          )}

          {/* Right side */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* Notification bell */}
                <div className="relative hidden md:block">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-ink-muted hover:text-ink hover:bg-cream-dark"
                    onClick={() => setNotifOpen((o) => !o)}
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-terra text-cream text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                  {notifOpen && (
                    <NotificationsDropdown onClose={() => setNotifOpen(false)} />
                  )}
                </div>

                {/* Avatar dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-terra focus:ring-offset-1">
                      <Avatar className="w-8 h-8 cursor-pointer">
                        <AvatarImage src={user.avatar_url} alt={user.display_name} />
                        <AvatarFallback className="bg-terra text-cream text-xs font-semibold">
                          {getInitials(user.display_name || user.username)}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-cream border-cream-dark">
                    <div className="px-3 py-2">
                      <p className="text-sm font-semibold text-ink">{user.display_name}</p>
                      <p className="text-xs text-ink-muted">@{user.username}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-cream-dark" />
                    <DropdownMenuItem
                      onClick={() => navigate(`/profile/${user.username}`)}
                      className="cursor-pointer text-ink hover:text-terra"
                    >
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/settings')}
                      className="cursor-pointer text-ink hover:text-terra"
                    >
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-cream-dark" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer text-ink-muted hover:text-terra"
                    >
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile hamburger */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden text-ink-muted hover:text-ink"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-ink-light hover:text-ink hover:bg-cream-dark text-sm"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-terra hover:bg-terra/90 text-cream text-sm"
                >
                  Join
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && user && (
        <div className="md:hidden bg-cream border-t border-cream-dark px-4 py-4 space-y-3">
          <BookAutocomplete
            onSelect={(book) => { setMobileMenuOpen(false); handleBookSelect(book); }}
            onSearch={(q) => { setMobileMenuOpen(false); handleSearch(q); }}
            placeholder="Search books..."
          />
          <button
            onClick={() => { navigate('/search'); setMobileMenuOpen(false); }}
            className="w-full flex items-center justify-center gap-2 bg-terra text-cream font-semibold rounded-full px-4 py-2 text-sm mb-2"
          >
            <Plus className="w-4 h-4" />
            Add Books
          </button>
          <nav className="space-y-1">
            <button
              onClick={() => { navigate('/feed'); setMobileMenuOpen(false); }}
              className="w-full text-left px-2 py-2 text-sm text-ink hover:text-terra font-medium"
            >
              Feed
            </button>
            <button
              onClick={() => { navigate(`/profile/${user.username}`); setMobileMenuOpen(false); }}
              className="w-full text-left px-2 py-2 text-sm text-ink hover:text-terra font-medium"
            >
              Profile
            </button>
            <button
              onClick={() => { navigate('/search'); setMobileMenuOpen(false); }}
              className="w-full text-left px-2 py-2 text-sm text-ink hover:text-terra font-medium"
            >
              Discover
            </button>
            <button
              onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}
              className="w-full text-left px-2 py-2 text-sm text-ink hover:text-terra font-medium"
            >
              Settings
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-2 py-2 text-sm text-ink-muted hover:text-terra font-medium"
            >
              Sign out
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
